import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DownloadLinkType } from '../types';
import { track } from './analytics';
import { useGetDownloadLink } from './useApiMutation';

export type FilePreviewState =
  | { status: 'loading' }
  /** isPdf comes from the file's contents, which can differ from what its name suggests. */
  | { status: 'ready'; blob: Blob; isPdf: boolean }
  | { status: 'error' }

/**
 * Fetches a portal file into memory so it can be shown in the preview dialog.
 * Fetching starts when `enabled` becomes true.
 *
 * The signed link the API returns expires within a minute, so the whole file is
 * read straight away rather than streamed in pieces as the user scrolls.
 * Reading it as a blob also sidesteps the `Content-Disposition: attachment` that
 * some files are stored with, which would otherwise force a download.
 */
export const useFilePreview = ({ enabled, FileName, CompanyIdOrderIdHash, Type, DocumentBatchId, DocumentFileName }: {
  enabled: boolean;
  FileName: string;
  CompanyIdOrderIdHash: string;
  Type: DownloadLinkType;
  DocumentBatchId?: string;
  DocumentFileName?: string;
}): FilePreviewState => {
  const { search } = useLocation()
  const { mutateAsync } = useGetDownloadLink({ CompanyIdOrderIdHash, search })
  const [state, setState] = useState<FilePreviewState>({ status: 'loading' })

  useEffect(() => {
    if (!enabled) return

    let cancelled = false
    setState({ status: 'loading' })
    track(`Preview ${Type} initialized`, FileName)

    const load = async () => {
      const { Url } = await mutateAsync({ FileName, Type, DocumentBatchId, DocumentFileName })
      const response = await fetch(Url)
      if (!response.ok) {
        throw new Error(`Fetching preview failed with status ${response.status}`)
      }
      const blob = await response.blob()
      return { blob, isPdf: await startsWithPdfSignature(blob) }
    }

    load().then(({ blob, isPdf }) => {
      if (cancelled) return
      setState({ status: 'ready', blob, isPdf })
      track(`Preview ${Type} complete`, FileName)
    }).catch(error => {
      if (cancelled) return
      setState({ status: 'error' })
      track('error', 'useFilePreview', {}, {
        message: error instanceof Error ? error.message : JSON.stringify(error),
        component: 'useFilePreview',
        FileName,
        Type,
      })
      console.log('[api] useFilePreview ', error)
    })

    return () => { cancelled = true }
  }, [enabled, mutateAsync, FileName, Type, DocumentBatchId, DocumentFileName])

  return state
}

async function startsWithPdfSignature(blob: Blob) {
  return await blob.slice(0, 5).text() === '%PDF-'
}
